/**
 * Lazy vendor loader.
 *
 * Splide, AOS, intl-tel-input and Swiper used to be fetched on every page load
 * — from the layout, or at DOMContentLoaded by sections sitting far below the
 * fold. This fetches each one on demand and caches the promise so repeat
 * callers share a single request.
 *
 *   window.loadVendor('splide').then(...)            // as soon as it is needed
 *   window.loadVendor('swiper', sectionEl).then(...) // once sectionEl nears the viewport
 *
 * Passing an element keeps the library's download and parse off the critical
 * path entirely until the user scrolls near the thing that needs it.
 */
(function () {
  var VENDORS = {
    splide: {
      js: 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js',
      css: 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css',
      ready: function () {
        return typeof window.Splide !== 'undefined';
      },
    },
    aos: {
      js: 'https://unpkg.com/aos@2.3.1/dist/aos.js',
      css: 'https://unpkg.com/aos@2.3.1/dist/aos.css',
      ready: function () {
        return typeof window.AOS !== 'undefined';
      },
    },
    iti: {
      js: 'https://cdn.jsdelivr.net/npm/intl-tel-input@17.0.19/build/js/intlTelInput.min.js',
      css: 'https://cdn.jsdelivr.net/npm/intl-tel-input@17.0.19/build/css/intlTelInput.min.css',
      ready: function () {
        return typeof window.intlTelInput !== 'undefined';
      },
    },
    swiper: {
      // The element bundle ships its own styles, so there is no separate CSS.
      js: 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-element-bundle.min.js',
      ready: function () {
        return !!(window.customElements && customElements.get('swiper-container'));
      },
    },
  };

  // Start fetching this far before the element scrolls into view, so the
  // library is ready by the time the section is actually on screen.
  var PRELOAD_MARGIN = '600px';

  var pending = {};

  function loadStylesheet(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = function () {
        reject(new Error('Failed to load ' + src));
      };
      document.head.appendChild(script);
    });
  }

  // Resolves once the element is within PRELOAD_MARGIN of the viewport.
  function whenNearViewport(el) {
    if (!el || !('IntersectionObserver' in window)) return Promise.resolve();
    return new Promise(function (resolve) {
      var observer = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        observer.disconnect();
        resolve();
      }, { rootMargin: PRELOAD_MARGIN });
      observer.observe(el);
    });
  }

  function fetchVendor(name, vendor) {
    if (vendor.ready()) return Promise.resolve();
    if (pending[name]) return pending[name];

    if (vendor.css) loadStylesheet(vendor.css);
    pending[name] = loadScript(vendor.js).catch(function (error) {
      pending[name] = null;
      throw error;
    });
    return pending[name];
  }

  window.loadVendor = function (name, waitForEl) {
    var vendor = VENDORS[name];
    if (!vendor) return Promise.reject(new Error('Unknown vendor: ' + name));
    if (vendor.ready()) return Promise.resolve();
    if (!waitForEl) return fetchVendor(name, vendor);

    return whenNearViewport(waitForEl).then(function () {
      return fetchVendor(name, vendor);
    });
  };

  // AOS has no call sites of its own — sections only add data-aos attributes,
  // so the loader owns both the fetch and the init.
  function initAos() {
    if (!document.querySelector('[data-aos]')) return;
    window
      .loadVendor('aos')
      .then(function () {
        window.AOS.init({ once: true });
      })
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAos);
  } else {
    initAos();
  }
})();
